import React, { useState } from 'react';
import { Bot, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import type { AuthorizedAgent } from '@/api/useAgentsApi';

interface Props {
    agent: AuthorizedAgent;
    orgs: { id: string; name: string }[];
    onRevoke: (agent: AuthorizedAgent) => void | Promise<void>;
    onSetOrgAccess: (agent: AuthorizedAgent, orgId: string, allowed: boolean) => Promise<void>;
    onSetPersonalAccess: (agent: AuthorizedAgent, allowed: boolean) => Promise<void>;
}

// Sentinel key for the personal-scope row in the per-row pending/spinner state.
const PERSONAL_KEY = '__personal__';

function formatRelative(dateString: string): string {
    const date = new Date(dateString);
    const diffMs = Date.now() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const AgentRow: React.FC<Props> = ({ agent, orgs, onRevoke, onSetOrgAccess, onSetPersonalAccess }) => {
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    // Which scope row (PERSONAL_KEY or an org id) currently has an in-flight
    // change — drives the inline spinner and disables toggles to avoid races.
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    const displayName = agent.clientName || agent.clientId;

    const scopeSummary = [
        agent.personalAllowed ? 'Personal' : null,
        agent.allowedOrgIds.length > 0 ? `${agent.allowedOrgIds.length} org${agent.allowedOrgIds.length === 1 ? '' : 's'}` : null,
    ]
        .filter(Boolean)
        .join(' + ');

    const runToggle = async (key: string, change: () => Promise<void>) => {
        setPendingKey(key);
        try {
            await change();
        } finally {
            setPendingKey(null);
        }
    };

    const busy = pendingKey !== null;

    const renderScopeRow = (key: string, label: string, sublabel: string, checked: boolean, onChange: (next: boolean) => void) => (
        <label
            key={key}
            className="flex items-center gap-2.5 text-sm text-neutral-700 dark:text-neutral-200 cursor-pointer select-none py-0.5"
        >
            <Checkbox
                className="h-[18px] w-[18px]"
                checked={checked}
                disabled={busy}
                onCheckedChange={next => onChange(next === true)}
                aria-label={`Allow ${displayName} in ${label}`}
            />
            <span className="truncate">{label}</span>
            <span className="text-xs text-neutral-400 dark:text-neutral-500">{sublabel}</span>
            {pendingKey === key && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" aria-label="Saving" />}
        </label>
    );

    return (
        <Card className="px-4 py-3 group">
            <div className="flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400">
                    <Bot className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">{displayName}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 shrink-0">
                            Active
                        </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-neutral-500 dark:text-neutral-400 flex-wrap">
                        <span>Last active {formatRelative(agent.lastSeenAt)}</span>
                        <span aria-hidden>·</span>
                        <span>{scopeSummary || 'No access'}</span>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {confirmRevoke ? (
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-red-600 hover:text-red-700"
                                onClick={() => {
                                    void onRevoke(agent);
                                    setConfirmRevoke(false);
                                }}
                            >
                                Confirm
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setConfirmRevoke(false)}>
                                Cancel
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-neutral-400 hover:text-red-600 dark:text-neutral-500 dark:hover:text-red-400 group-hover:text-red-500 dark:group-hover:text-red-400 transition-colors"
                            onClick={() => setConfirmRevoke(true)}
                        >
                            Revoke
                        </Button>
                    )}
                </div>
            </div>
            <div className="mt-3 pl-14 flex flex-col gap-1">
                {renderScopeRow(PERSONAL_KEY, 'Personal', 'your graphs with no org', agent.personalAllowed, next =>
                    runToggle(PERSONAL_KEY, () => onSetPersonalAccess(agent, next)),
                )}
                {orgs.length === 0 ? (
                    <span className="text-xs text-neutral-400 dark:text-neutral-500">You're not a member of any organizations.</span>
                ) : (
                    orgs.map(org =>
                        renderScopeRow(org.id, org.name, 'organization', agent.allowedOrgIds.includes(org.id), next =>
                            runToggle(org.id, () => onSetOrgAccess(agent, org.id, next)),
                        ),
                    )
                )}
            </div>
        </Card>
    );
};
